import { updateHomeworkShoppingTabs } from "./api/ProcurementClient";
import Rfq from "./models/Rfq";
import { RfqHomework } from "./models/RfqHomework";

export interface ShoppingTabPersistLine {
  itemId: number;
  vendorUrls: string[];
  focusedTabUrl?: string | null;
}

function mergeHomeworkIntoRfqs(
  rfqs: Rfq[],
  savedHomework: RfqHomework
): Rfq[] {
  return rfqs.map((rfq) => ({
    ...rfq,
    quotes: rfq.quotes.map((hw) => {
      if (hw.id !== savedHomework.id) {
        return hw;
      }
      const existingItemsById = new Map(hw.items.map((item) => [item.id, item]));
      return {
        ...savedHomework,
        items: savedHomework.items.map((savedItem) => {
          const existing = existingItemsById.get(savedItem.id);
          if (!existing) {
            return savedItem;
          }
          const savedCandidates = savedItem.shoppingCandidates ?? [];
          const existingCandidates = existing.shoppingCandidates ?? [];
          return {
            ...savedItem,
            shoppingCandidates:
              savedCandidates.length > 0
                ? savedCandidates
                : existingCandidates,
          };
        }),
      };
    }),
  }));
}

export async function persistShoppingTabLines(
  homeworkId: number,
  lines: ShoppingTabPersistLine[],
  setRfqs: (rfqs: Rfq[] | ((prev: Rfq[]) => Rfq[])) => void
): Promise<void> {
  if (!Number.isFinite(homeworkId) || homeworkId <= 0 || !lines.length) {
    return;
  }
  const savedHomework = await updateHomeworkShoppingTabs(homeworkId, lines);
  setRfqs((prev) => mergeHomeworkIntoRfqs(prev, savedHomework));
}

export function registerShoppingTabPersistenceListener(
  setRfqs: (rfqs: Rfq[] | ((prev: Rfq[]) => Rfq[])) => void
): () => void {
  const handler = (event: Event) => {
    const detail = (
      event as CustomEvent<{
        homeworkId?: number;
        lines?: ShoppingTabPersistLine[];
      }>
    ).detail;
    const homeworkId = Number(detail?.homeworkId);
    const lines = detail?.lines;
    if (
      !Number.isFinite(homeworkId) ||
      homeworkId <= 0 ||
      !Array.isArray(lines) ||
      lines.length === 0
    ) {
      return;
    }
    void persistShoppingTabLines(homeworkId, lines, setRfqs).catch((err) => {
      console.error("Failed to persist shopping tabs", err);
    });
  };

  window.addEventListener("shoppingTabsPersist", handler);
  return () => window.removeEventListener("shoppingTabsPersist", handler);
}
